"use client";

import { START_MANUAL_SENTRY_SESSION_MUTATION } from "@/graphql/mutations/streamDeckMutations";
import { ADMIN_SENTRY_SESSION_STATUS_QUERY } from "@/graphql/queries/streamDeckQueries";
import { SENTRY_SESSION_STATUS_SUBSCRIPTION } from "@/graphql/subscriptions/streamDeckSubscriptions";
import { useMutation, useQuery, useSubscription } from "@apollo/client";
import clsx from "clsx";
import { useEffect, useState } from "react";
import {
    FiAlertCircle,
    FiClock,
    FiEye,
    FiPlay,
    FiUsers,
    FiZap,
} from "react-icons/fi";

interface SentrySessionStatus {
    sessionId: string | null;
    hasActiveSession: boolean;
    hasActiveStreamSession: boolean;
    startTime: string | null;
    endTime: string | null;
    doubleSentry: boolean;
    checkinCount: number;
}

export default function StreamDeckPage() {
    const [durationMinutes, setDurationMinutes] = useState(10);
    const [doubleSentryOverride, setDoubleSentryOverride] = useState<
        boolean | null
    >(null);
    const [sendChatMessage, setSendChatMessage] = useState(true);
    const [startingSession, setStartingSession] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [timeRemaining, setTimeRemaining] = useState<string | null>(null);
    const [subscriptionData, setSubscriptionData] =
        useState<SentrySessionStatus | null>(null);

    // Fetch initial sentry session status
    const { data, loading, error } = useQuery(
        ADMIN_SENTRY_SESSION_STATUS_QUERY,
        {
            fetchPolicy: "cache-and-network",
        },
    );

    // Subscribe to real-time updates
    useSubscription(SENTRY_SESSION_STATUS_SUBSCRIPTION, {
        onData: ({ data: subData }) => {
            console.debug(
                "[StreamDeckPage] Sentry subscription data received:",
                subData,
            );
            if (subData?.data?.sentrySessionStatus) {
                console.debug(
                    "[StreamDeckPage] Has active session:",
                    subData.data.sentrySessionStatus.hasActiveSession,
                );
                console.debug(
                    "[StreamDeckPage] Session ID:",
                    subData.data.sentrySessionStatus.sessionId,
                );
                // Update state with subscription data
                setSubscriptionData(subData.data.sentrySessionStatus);
            }
        },
    });

    // Mutation to start sentry session
    const [startSentrySession] = useMutation(
        START_MANUAL_SENTRY_SESSION_MUTATION,
        {
            onCompleted: (data) => {
                setStartingSession(false);
                if (data.startManualSentrySession.success) {
                    setSuccessMessage(data.startManualSentrySession.message);
                    setErrorMessage(null);
                    // No refetch needed - subscription will update automatically
                    setTimeout(() => setSuccessMessage(null), 5000);
                }
            },
            onError: (error) => {
                setStartingSession(false);
                setErrorMessage(error.message);
                setSuccessMessage(null);
                setTimeout(() => setErrorMessage(null), 5000);
            },
        },
    );

    // Use subscription data if available, fallback to query data
    const sentryStatus: SentrySessionStatus | null =
        subscriptionData ?? data?.adminSentrySessionStatus;

    // Calculate time remaining for active session
    useEffect(() => {
        if (!sentryStatus?.hasActiveSession || !sentryStatus.endTime) {
            setTimeRemaining(null);
            return;
        }

        const calculateTimeRemaining = () => {
            const endTime = new Date(sentryStatus.endTime!);
            const now = new Date();
            const diff = endTime.getTime() - now.getTime();

            if (diff <= 0) {
                setTimeRemaining("Expired");
                return;
                return;
            }

            const minutes = Math.floor(diff / 60000);
            const seconds = Math.floor((diff % 60000) / 1000);
            setTimeRemaining(
                `${minutes}:${seconds.toString().padStart(2, "0")}`,
            );
        };

        calculateTimeRemaining();
        const interval = setInterval(calculateTimeRemaining, 1000);

        return () => clearInterval(interval);
    }, [sentryStatus?.hasActiveSession, sentryStatus?.endTime]);

    const handleStartSentrySession = async () => {
        setStartingSession(true);
        setErrorMessage(null);
        setSuccessMessage(null);

        await startSentrySession({
            variables: {
                durationMinutes,
                doubleSentry: doubleSentryOverride,
                sendChatMessage,
            },
        });
    };

    if (loading) {
        return (
            <div className="mx-auto mt-4 w-full px-4">
                <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <div className="flex items-center justify-center p-8">
                        <div className="text-slate-600 dark:text-slate-400">
                            Loading stream deck...
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mx-auto mt-4 w-full px-4">
                <div className="rounded-2xl border border-red-300 bg-red-50 p-6 shadow-lg dark:border-red-700 dark:bg-red-900/20">
                    <div className="text-red-800 dark:text-red-400">
                        Error loading stream deck: {error.message}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto mt-4 w-full px-4">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                {/* LEFT: Stream Deck Controls */}
                <div className="space-y-6">
                    {/* Sentry Session Card */}
                    <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                        <div className="mb-4 flex items-center gap-2">
                            <FiEye className="h-6 w-6 text-slate-700 dark:text-slate-300" />
                            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                                Sentry Session
                            </h2>
                        </div>

                        {/* Current Status */}
                        <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-800">
                            {/* Stream Status */}
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Stream
                                </span>
                                {sentryStatus?.hasActiveStreamSession ? (
                                    <span className="flex items-center gap-2 rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-800 dark:bg-red-900/30 dark:text-red-400">
                                        <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
                                        Live
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2 rounded-full bg-slate-200 px-3 py-1 text-sm font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-400">
                                        <span className="h-2 w-2 rounded-full bg-slate-400" />
                                        Offline
                                    </span>
                                )}
                            </div>

                            {/* Sentry Status */}
                            <div className="mt-3 flex items-center justify-between border-t border-slate-200 pt-3 dark:border-slate-600">
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Sentry Session
                                </span>
                                {sentryStatus?.hasActiveSession ? (
                                    <span className="flex items-center gap-2 rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                        <span className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
                                        Active
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2 rounded-full bg-slate-200 px-3 py-1 text-sm font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-400">
                                        <span className="h-2 w-2 rounded-full bg-slate-400" />
                                        Inactive
                                    </span>
                                )}
                            </div>

                            {sentryStatus?.hasActiveSession && (
                                <div className="mt-4 grid grid-cols-2 gap-4">
                                    <div className="flex items-center gap-2">
                                        <FiClock className="h-4 w-4 text-slate-500" />
                                        <div>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                                Time Remaining
                                            </p>
                                            <p className="font-mono text-lg font-semibold text-slate-900 dark:text-slate-100">
                                                {timeRemaining || "--:--"}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <FiUsers className="h-4 w-4 text-slate-500" />
                                        <div>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                                Check-ins
                                            </p>
                                            <p className="font-mono text-lg font-semibold text-slate-900 dark:text-slate-100">
                                                {sentryStatus.checkinCount}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-span-2 flex items-center gap-2">
                                        <FiZap className="h-4 w-4 text-slate-500" />
                                        <div>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                                Double Sentry
                                            </p>
                                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                                {sentryStatus.doubleSentry
                                                    ? "Enabled"
                                                    : "Disabled"}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Start Session Options */}
                        {!sentryStatus?.hasActiveSession &&
                            sentryStatus?.hasActiveStreamSession && (
                                <div className="space-y-4">
                                    {/* Duration Selector */}
                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                            Duration (minutes)
                                        </label>
                                        <div className="flex gap-2">
                                            {[5, 10, 15, 20].map((mins) => (
                                                <button
                                                    key={mins}
                                                    onClick={() =>
                                                        setDurationMinutes(mins)
                                                    }
                                                    className={clsx(
                                                        "rounded-md px-4 py-2 text-sm font-medium transition-colors",
                                                        durationMinutes === mins
                                                            ? "bg-brand text-white"
                                                            : "bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600",
                                                    )}
                                                >
                                                    {mins}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Double Sentry Override */}
                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                            Double Sentry
                                        </label>
                                        <div className="flex gap-2">
                                            {[
                                                {
                                                    value: null,
                                                    label: "Use Settings",
                                                },
                                                {
                                                    value: true,
                                                    label: "Force On",
                                                },
                                                {
                                                    value: false,
                                                    label: "Force Off",
                                                },
                                            ].map((option) => (
                                                <button
                                                    key={String(option.value)}
                                                    onClick={() =>
                                                        setDoubleSentryOverride(
                                                            option.value,
                                                        )
                                                    }
                                                    className={clsx(
                                                        "rounded-md px-4 py-2 text-sm font-medium transition-colors",
                                                        doubleSentryOverride ===
                                                            option.value
                                                            ? "bg-brand text-white"
                                                            : "bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600",
                                                    )}
                                                >
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Send Chat Message Toggle */}
                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                            Send Twitch Chat Message
                                        </label>
                                        <div className="flex items-center space-x-3">
                                            <button
                                                onClick={() =>
                                                    setSendChatMessage(
                                                        !sendChatMessage,
                                                    )
                                                }
                                                className={clsx(
                                                    "focus:ring-brand relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none dark:focus:ring-offset-slate-900",
                                                    sendChatMessage
                                                        ? "bg-brand"
                                                        : "bg-slate-300 dark:bg-slate-600",
                                                )}
                                            >
                                                <span
                                                    className={clsx(
                                                        "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                                                        sendChatMessage
                                                            ? "translate-x-6"
                                                            : "translate-x-1",
                                                    )}
                                                />
                                            </button>
                                            <span className="text-sm text-slate-600 dark:text-slate-400">
                                                {sendChatMessage ? "Yes" : "No"}
                                            </span>
                                        </div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                            Announces sentry session to Twitch
                                            chat when enabled
                                        </p>
                                    </div>

                                    {/* Start Button */}
                                    <button
                                        onClick={handleStartSentrySession}
                                        disabled={startingSession}
                                        className={clsx(
                                            "flex w-full items-center justify-center gap-2 rounded-lg px-6 py-3 text-lg font-semibold transition-colors",
                                            startingSession
                                                ? "cursor-not-allowed bg-slate-300 text-slate-500 dark:bg-slate-700 dark:text-slate-500"
                                                : "bg-green-600 text-white hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600",
                                        )}
                                    >
                                        <FiPlay className="h-5 w-5" />
                                        {startingSession
                                            ? "Starting..."
                                            : "Start Sentry Session"}
                                    </button>
                                </div>
                            )}

                        {/* Disabled State Message */}
                        {sentryStatus?.hasActiveSession && (
                            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
                                <FiAlertCircle className="h-5 w-5 shrink-0" />
                                <p className="text-sm">
                                    A sentry session is currently active. Wait
                                    for it to end before starting a new one.
                                </p>
                            </div>
                        )}

                        {/* Stream Offline Message */}
                        {!sentryStatus?.hasActiveSession &&
                            !sentryStatus?.hasActiveStreamSession && (
                                <div className="flex items-center gap-2 rounded-lg border border-slate-300 bg-slate-100 p-4 text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400">
                                    <FiAlertCircle className="h-5 w-5 shrink-0" />
                                    <p className="text-sm">
                                        The stream is currently offline. An
                                        active stream session is required to
                                        start a sentry session.
                                    </p>
                                </div>
                            )}

                        {/* Messages */}
                        {successMessage && (
                            <div className="mt-4 rounded-lg bg-green-100 p-3 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                                <p className="text-sm font-medium">
                                    {successMessage}
                                </p>
                            </div>
                        )}
                        {errorMessage && (
                            <div className="mt-4 rounded-lg bg-red-100 p-3 text-red-800 dark:bg-red-900/20 dark:text-red-400">
                                <p className="text-sm font-medium">
                                    {errorMessage}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Placeholder for future deck buttons */}
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 dark:border-slate-700 dark:bg-slate-800/50">
                        <p className="text-center text-sm text-slate-500 dark:text-slate-400">
                            More stream deck controls coming soon...
                        </p>
                    </div>
                </div>

                {/* RIGHT: Documentation Panel */}
                <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <h2 className="mb-4 text-2xl font-bold text-slate-900 dark:text-slate-100">
                        Stream Deck Guide
                    </h2>

                    <div className="space-y-4 text-sm leading-relaxed text-slate-700 dark:text-slate-400">
                        <div>
                            <h3 className="mb-2 font-semibold text-slate-900 dark:text-slate-100">
                                Sentry Sessions
                            </h3>
                            <p>
                                Sentry sessions allow viewers to check in during
                                the stream to earn VP and watch time. Use this
                                control to manually trigger a sentry session
                                when needed.
                            </p>
                        </div>

                        <div>
                            <h3 className="mb-2 font-semibold text-slate-900 dark:text-slate-100">
                                Duration
                            </h3>
                            <p>
                                Choose how long the sentry session should last.
                                The default is 10 minutes. After this time, the
                                session will automatically end and no more
                                check-ins will be accepted.
                            </p>
                        </div>

                        <div>
                            <h3 className="mb-2 font-semibold text-slate-900 dark:text-slate-100">
                                Double Sentry
                            </h3>
                            <p>
                                When enabled, players earn double rewards: 100
                                VP instead of 50 VP, and 2 hours of watch time
                                instead of 1. By default, this uses the global
                                stream setting, but you can override it per
                                session.
                            </p>
                        </div>

                        <div>
                            <h3 className="mb-2 font-semibold text-slate-900 dark:text-slate-100">
                                Chat Message
                            </h3>
                            <p>
                                When enabled, a message will be sent to Twitch
                                chat announcing the sentry session. Disable this
                                if you want a silent session.
                            </p>
                        </div>

                        <div className="rounded-lg bg-blue-50 p-3 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                            <p className="text-xs">
                                <strong>Requirements:</strong> An active stream
                                session must exist for sentry sessions to work.
                                The session is automatically created when the
                                stream goes live.
                            </p>
                        </div>

                        <div className="bg-brand/10 text-brand dark:bg-brand/10 dark:text-brand rounded-lg p-3">
                            <p className="text-xs">
                                <strong>Note:</strong> Only one sentry session
                                can be active at a time. The button will be
                                disabled while a session is running.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
